import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useApolloClient } from '@apollo/client';
import { Grid, FormControlLabel, Checkbox } from '@mui/material';
import { Autocomplete, Button, TextField } from '@totalsoft/rocket-ui';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { useMutation, useSubscription } from 'hooks/apollo';
import { useTranslation } from 'react-i18next';
import { nonWorkingDaysModalVar } from '../../variables';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { getErrors, isValid, useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { Prompt } from 'react-router-dom';
import { useToast } from 'hooks/toasts/useToast';
import { ADMIN_NON_WORKING_DAYS_WRITE } from 'constants/rightsCodes';
import { usePermission } from 'providers/RightsHooks';
import { DateTime } from '@ts-private/rocket-extensions';
import { emptyString } from 'utils/constants';
import { onCheckBoxChange } from 'utils/propertyChangeAdapters';
import { SAVE_NON_WORKING_DAY } from '../mutations';
import { NON_WORKING_DAY_SAVED } from '../subscriptions';
import { nonWorkingDayValidator } from '../validators';
import { nonWorkingDaysStore } from '../../list/nonWorkingDaysStore';
import { useListBrowserUtils } from '@ts-private/list-browser';
import { ThemedWrapper } from 'components/common/SurfaceStyles';

const NonWorkingDayModalComponent = ({ nonWorkingDay, dictionaryContext, sites }) => {
  const client = useApolloClient();
  const addToast = useToast();
  const { t } = useTranslation();
  const canWrite = usePermission(ADMIN_NON_WORKING_DAYS_WRITE);
  const { store } = useListBrowserUtils(nonWorkingDaysStore);

  const [saving, setSaving] = useState(false);
  const [nonWorkingDayLens, dirtyInfo] = useChangeTrackingLens(nonWorkingDay);

  const nonWorkingDaysValidator = useMemo(() => nonWorkingDayValidator(store.allValues), [store.allValues]);
  const [validation, validate] = useDirtyFieldValidation(nonWorkingDaysValidator);

  const allSites = useMemo(() => {
    const additionalSite = { siteId: -1, siteName: t('NonWorkingDays.AllSites') };
    return [...sites.sites, additionalSite];
  }, [sites, t]);

  const selectedContexts = useMemo(() => {
    if (!dictionaryContext || !dictionaryContext.nonWorkingDayContexts) return [];
    return dictionaryContext.nonWorkingDayContexts.filter(context => {
      const contexts = get(nonWorkingDayLens.contexts);
      return Array.isArray(contexts) && contexts.includes(context.dictionaryItemId);
    });
  }, [dictionaryContext, nonWorkingDayLens.contexts]);

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  useEffect(() => {
    validate(get(nonWorkingDayLens), dirtyInfo);
  }, [validate, dirtyInfo, nonWorkingDayLens]);

  const [saveNonWorkingDay] = useMutation(SAVE_NON_WORKING_DAY, {
    onError: () => {
      setSaving(false);
    }
  });

  const handleSave = useCallback(() => {
    const input = get(nonWorkingDayLens);
    const inputSave = { ...input, contexts: input.validInAllContexts ? [] : input.contexts };

    if (!validate(inputSave)) return;
    saveNonWorkingDay({ variables: { input: inputSave } });
    setSaving(true);
  }, [nonWorkingDayLens, saveNonWorkingDay, validate]);

  useSubscription(NON_WORKING_DAY_SAVED, {
    onData: _ => {
      setSaving(false);
      nonWorkingDaysModalVar({ open: false });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'nonWorkingDays' });
      client.cache.gc();
      addToast(t('NonWorkingDays.NonWorkingDaySaved'), 'success');
    }
  });

  return (
    <>
      <Prompt when={isDirty} message={t('LeavingWithoutSaving')} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <DateTime
            required
            name='nonWorkingDayDate'
            timeFormat={false}
            label={t('NonWorkingDays.Date')}
            value={get(nonWorkingDayLens.nonWorkingDayDate) || emptyString}
            onChange={event => set(nonWorkingDayLens.nonWorkingDayDate)(event)}
            disabled={!canWrite}
            error={!isValid(validation.nonWorkingDayDate)}
            helperText={getErrors(validation.nonWorkingDayDate)}
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            disabled={!canWrite}
            fullWidth
            label={t('NonWorkingDays.WorkingSite')}
            valueKey={'siteId'}
            labelKey={'siteName'}
            value={get(nonWorkingDayLens.workingSiteId)}
            onChange={set(nonWorkingDayLens.workingSiteId)}
            options={allSites}
            simpleValue
            required
            error={!isValid(validation.workingSiteId)}
            helperText={getErrors(validation.workingSiteId)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            disabled={!canWrite}
            fullWidth
            label={t('NonWorkingDays.Description')}
            value={get(nonWorkingDayLens.description) || emptyString}
            onChange={set(nonWorkingDayLens.description)}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={get(nonWorkingDayLens.workingDay) || false}
                onClick={nonWorkingDayLens.workingDay |> set |> onCheckBoxChange}
                disabled={!canWrite}
              />
            }
            label={t('NonWorkingDays.WorkingDay')}
            labelPlacement='end'
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={get(nonWorkingDayLens.validInAllContexts) || false}
                onClick={nonWorkingDayLens.validInAllContexts |> set |> onCheckBoxChange}
                disabled={!canWrite}
              />
            }
            label={t('NonWorkingDays.ValidInAllContexts')}
            labelPlacement='end'
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            fullWidth
            value={!get(nonWorkingDayLens.validInAllContexts) === true ? selectedContexts : []}
            valueKey='dictionaryItemId'
            labelKey='dictionaryItemName'
            isMultiSelection={true}
            options={dictionaryContext.nonWorkingDayContexts}
            label={t('NonWorkingDays.Contexts')}
            onChange={value => set(nonWorkingDayLens.contexts)(value.map(option => option.dictionaryItemId))}
            isSearchable={true}
            isClearable={true}
            disabled={!canWrite || get(nonWorkingDayLens.validInAllContexts) === true}
          />
        </Grid>
        {(!isValid(validation) || !isValid(validation.validInAllContexts) || !isValid(validation.contexts)) && (
          <Grid item xs={12}>
            <ThemedWrapper color='error'>
              {getErrors(validation)}
              {getErrors(validation.validInAllContexts)}
              {getErrors(validation.contexts)}
            </ThemedWrapper>
          </Grid>
        )}
        <Grid container item xs={12} justifyContent='flex-end'>
          <Button sx={{ mr: 1 }} disabled={saving || !canWrite || !isDirty} onClick={handleSave}>
            {saving ? t('Saving') : t('Save')}
          </Button>
          <Button onClick={() => nonWorkingDaysModalVar({ open: false })}>{t('Close')}</Button>
        </Grid>
      </Grid>
    </>
  );
};

NonWorkingDayModalComponent.propTypes = {
  nonWorkingDay: PropTypes.object.isRequired,
  dictionaryContext: PropTypes.object,
  sites: PropTypes.object
};

export default NonWorkingDayModalComponent;
